/**
 * Pure decisions behind the per-company count on the org tabs (Vincent,
 * 2026-10-22: "le # de requête en attente par Co"). Kept free of Angular and
 * Firestore so they are unit-testable; OrgUnreadService applies them.
 */

import { isRosterAgentConversation, RosterKeys } from 'src/app/services/teams/roster-keys';

/** The parts of a conversation doc the background count reads. */
export interface BackgroundConversation {
  id: string;
  category: 'ai' | 'channel' | 'personal' | string;
  organizationId?: string | null;
  closed?: boolean;
  /** The agent DM's agent (conversation `agentId`). */
  agentId?: string | null;
  /** A legacy shared-agent thread (a channel the rail lists under Agents). */
  sharedAgentId?: string | null;
  lastMessage?: { id?: string; sender?: string; teamTraffic?: boolean } | null;
}

/** What the rail of a BACKGROUND org would show, from that org's project docs. */
export interface RailVisibility {
  /** Project managers — hidden from the Agents roster. */
  managers: RosterKeys;
  /** Project spaces of the projects the person is in. */
  projectSpaceIds: ReadonlySet<string>;
}

/**
 * Does this conversation of a BACKGROUND org count toward its tab? Exactly the
 * rows the rail holds when that org is active (SidebarNavService: the Agents
 * roster + legacy shared agents + the Projects section), so the tab never
 * shows a number the rail hides — Hall of Cards showed 7 from two project
 * managers' DMs (Vincent, 2026-10-22). Direct discussions have no rail row.
 */
export function railShowsInBackground(c: BackgroundConversation, rail: RailVisibility): boolean {
  if (c.closed) return false;
  if (c.category === 'ai') return isRosterAgentConversation(c, rail.managers);
  if (c.category === 'channel') return rail.projectSpaceIds.has(c.id) || !!c.sharedAgentId;
  return false;
}

/**
 * Must the count be re-read for this conversation, or does the previous one
 * stand? Mirrors the rail (MessagingListenerService.settleCount): nothing new
 * when the last message did not change; the person's own last message clears
 * it; a teamed agent talking to its team (teamTraffic) adds nothing.
 */
export function nextCountDecision(
  c: BackgroundConversation,
  previous: { lastMessageId?: string; count: number } | undefined,
  uid: string,
): { kind: 'keep'; count: number } | { kind: 'zero' } | { kind: 'query' } {
  const last = c.lastMessage;
  if (previous && previous.lastMessageId === (last?.id ?? undefined)) return { kind: 'keep', count: previous.count };
  if (!last?.id || last.sender === uid) return { kind: 'zero' };
  if (last.teamTraffic && previous) return { kind: 'keep', count: previous.count };
  return { kind: 'query' };
}

/** An org's total from its conversations' counts (each capped at 10 = "9+", like the tab title). */
export function orgTotal(counts: Iterable<number>): number {
  let total = 0;
  for (const n of counts) total += Math.max(0, Math.min(n, 10));
  return total;
}

/** The tab's circle: undefined for 0, 1–9, '9+' above 9. */
export function tabCount(total: number): number | '9+' | undefined {
  if (!total || total < 1) return undefined;
  return total > 9 ? '9+' : Math.floor(total);
}
